import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { Badge, Button, Modal, PageHead } from "../../components/ui";
import { SportCheck, SportRun, SportStrength, SportTreadmill, SportWalk } from "../../components/sportIcons";
import { formatDuration, formatPace, metersToKm } from "../../lib/format";
import {
  activeMs, avgPaceSPerKm, buildBody, elapsedMs, fixAgeMs, hasEnoughRoute, signalQuality, type Problem, type SignalQuality, type Status,
} from "../../lib/gps";
import { interrupted, isExpired } from "../../lib/gpsSync";
import { useCaptureContext, useGpsRecorder } from "../../lib/useGpsRecorder";
import { loadTimed } from "../../lib/timed";
import { navigate } from "../../router/router";
import { TimedRecorder } from "./TimedRecorder";
import { t, tn } from "../../i18n";

/**
 * v0.16.4 (B5) — tela de gravação do Modo Corrida, só em aparelho com o dedo como entrada principal.
 * Captura, armazenamento e envio vêm de `useGpsRecorder` (B3/B4).
 * v0.18.0: no app o GPS continua com a tela bloqueada (serviço nativo); no navegador a tela precisa ficar acesa.
 * Ao salvar, uma janela oferece "Ver atividade"; depois disso a corrida é vista só na aba Atividades (sem lista aqui).
 */

const STATUS: Record<Status, () => string> = {
  idle: () => t("Pronto para começar"), checking: () => t("Verificando"), requesting_location: () => t("Aguardando o sinal de GPS"),
  recording: () => t("Gravando"), paused: () => t("Pausado"), finalizing: () => t("Revise e salve"), saved: () => t("Salva no aparelho"),
  error: () => t("Não foi possível começar"),
};

const PROBLEM: Record<Exclude<Problem, null>, () => string> = {
  insecure: () => t("Esta página não está em HTTPS confiável, então o GPS não funciona."),
  unsupported: () => t("Este navegador não tem geolocalização."),
  denied: () => t("Permissão de localização negada. Libere nas configurações do navegador e tente de novo."),
  unavailable: () => t("Sem posição no momento. Vá para um local aberto."),
  timeout: () => t("Sem sinal de GPS há alguns segundos."),
  storage: () => t("Este navegador não permite guardar a gravação no aparelho, então não vamos gravar sem essa proteção."),
  full: () => t("Há gravações demais aguardando envio neste aparelho. Resolva as pendentes antes de iniciar outra."),
  interrupted: () => t("Há uma gravação interrompida. Retome ou descarte antes de iniciar outra."),
};

const QUALITY: Record<SignalQuality, () => string> = {
  good: () => t("Sinal bom"), fair: () => t("Sinal razoável"), weak: () => t("Sinal fraco"), poor: () => t("Sinal impreciso"), none: () => t("Sem sinal"),
};

type Sport = "run" | "walk" | "treadmill" | "strength";
type GpsSport = "run" | "walk";
const isTimed = (s: Sport): s is "treadmill" | "strength" => s === "treadmill" || s === "strength";

/** Escolha do tipo de treino: círculos só com o desenho; o nome fica em aria-label/title para leitor de tela. */
const SPORTS: { v: Sport; label: () => string; Icon: typeof SportRun }[] = [
  { v: "run", label: () => t("Corrida"), Icon: SportRun },
  { v: "treadmill", label: () => t("Esteira"), Icon: SportTreadmill },
  { v: "walk", label: () => t("Caminhada"), Icon: SportWalk },
  { v: "strength", label: () => t("Fortalecimento"), Icon: SportStrength },
];

function SportPicker({ sport, onChange }: { sport: Sport; onChange: (s: Sport) => void }) {
  return (
    <fieldset className="sport-picker">
      <legend className="visually-hidden">{t("Tipo de atividade")}</legend>
      {SPORTS.map(({ v, label, Icon }) => (
        <label key={v} className={`sport-option${sport === v ? " on" : ""}`} title={label()}>
          <input type="radio" name="sport" aria-label={label()} checked={sport === v} onChange={() => onChange(v)} />
          <Icon />
          {sport === v && <SportCheck className="sport-check" />}
        </label>
      ))}
    </fieldset>
  );
}

export function RunModePage({ orgId }: { orgId: string }) {
  const ctx = useCaptureContext();
  return (
    <>
      <PageHead title={t("Gravar corrida")} subtitle={t("Escolha o tipo de treino e registre.")} />
      {ctx.allowed ? <Recorder orgId={orgId} /> : <Unavailable reason={ctx.reason} />}
    </>
  );
}

function Unavailable({ reason }: { reason: "desktop" | "app" | null }) {
  return (
    <div className="run run-note" role="status">
      {reason === "app" ? (
        <>
          <p><strong>{t("A gravação ainda não está disponível dentro do aplicativo.")}</strong></p>
          <p>{t("Abra o endereço do EAPace no navegador do celular, entre com a sua conta e grave por lá. Suas atividades aparecem aqui normalmente.")}</p>
        </>
      ) : (
        <>
          <p><strong>{t("A gravação é feita só no celular.")}</strong></p>
          <p>{t("O computador não tem um GPS confiável. Abra o EAPace no navegador do celular, entre com a sua conta e toque em Gravar corrida. A atividade aparece aqui depois do envio.")}</p>
        </>
      )}
    </div>
  );
}

function Recorder({ orgId }: { orgId: string }) {
  const { me } = useAuth();
  const g = useGpsRecorder(orgId, me?.id ?? "");
  const { r, now } = g;
  const [sport, setSport] = useState<Sport>(() => loadTimed(window.localStorage, me?.id ?? "")?.sport ?? "run");
  const [timedActive, setTimedActive] = useState(false);
  const [askDiscard, setAskDiscard] = useState(false);
  const [dropId, setDropId] = useState<string | null>(null);
  const [announce, setAnnounce] = useState("");
  const [savedOpen, setSavedOpen] = useState(false);
  const savedId = useRef<string | null>(null);
  const lastSplits = useRef(0);

  const act = activeMs(r, now);
  const dist = r.tracker.distanceM;
  const pace = avgPaceSPerKm(dist, act);
  const age = fixAgeMs(r, now);
  const quality = signalQuality(r.lastAccuracy, age);
  const live = r.status === "recording" || r.status === "paused" || r.status === "finalizing";
  const halfway = interrupted(g.sessions).filter((x) => x.id !== r.clientId);
  // só o que pede ação: recusadas pelo servidor ou antigas demais (as pendentes são enviadas sozinhas)
  const problems = g.sessions.filter((x) => x.phase === "rejected" || (x.phase === "pending_sync" && isExpired(x, now)));
  const pendingCount = g.sessions.filter((x) => x.phase === "pending_sync" && !isExpired(x, now)).length;
  const saved = savedId.current ? g.sessions.find((x) => x.id === savedId.current) : undefined;
  const savedActivity = g.sentDirect ?? (saved?.phase === "synced" ? saved.activityId ?? null : null);
  const enough = hasEnoughRoute(r);
  const body = r.status === "finalizing" ? buildBody(r, sport as GpsSport) : null;

  // anuncia cada parcial para leitor de tela (o cronômetro não é anunciado a cada segundo)
  useEffect(() => {
    if (r.splits.length > lastSplits.current) {
      const s = r.splits[r.splits.length - 1];
      setAnnounce(t("Parcial {km} km em {pace}.", { km: s.km, pace: formatPace(s.paceSPerKm) }));
    }
    lastSplits.current = r.splits.length;
  }, [r.splits]);

  const title = useRef<HTMLHeadingElement>(null);
  useEffect(() => { if (r.status === "finalizing") title.current?.focus(); }, [r.status]);

  return (
    <div className="run">
      <div className="run-state">
        <Badge tone={r.status === "recording" ? "ok" : r.status === "error" ? "danger" : r.status === "paused" ? "warn" : "off"}>{r.status === "saved" && g.sentDirect ? t("Enviada") : STATUS[r.status]()}</Badge>
        {(r.status === "recording" || r.status === "requesting_location") && <span className={`run-signal ${quality}`}>{QUALITY[quality]()}{r.lastAccuracy !== null && quality !== "none" ? ` · ${Math.round(r.lastAccuracy)} m` : ""}</span>}
        {g.syncing && <span className="muted" role="status">{t("Enviando…")}</span>}
      </div>
      <p className="visually-hidden" role="status" aria-live="polite">{STATUS[r.status]()}{announce ? `. ${announce}` : ""}</p>

      {g.storage === "unavailable" && <div className="run-note danger" role="alert"><p>{t("Este navegador não permite guardar dados no aparelho: a gravação ficaria só na memória e seria perdida ao recarregar.")}</p></div>}
      {g.persistFull && <div className="run-note danger" role="alert"><p>{t("Esta gravação atingiu o limite de armazenamento local. Finalize agora.")}</p></div>}
      {g.persistError && <div className="run-note danger" role="alert"><p>{t("O aparelho não está conseguindo guardar a gravação (armazenamento cheio ou bloqueado). Libere espaço no celular; continuamos tentando. Enquanto isso, ela existe só nesta página: não feche nem recarregue.")}</p></div>}
      {g.saveError && r.status === "finalizing" && <div className="run-note danger" role="alert"><p>{t("Não foi possível guardar a gravação no aparelho nem enviá-la agora. Nada foi apagado: libere espaço ou conecte-se à internet e toque em Salvar atividade de novo.")}</p></div>}
      {r.problem && <div className="run-note danger" role="alert"><p>{PROBLEM[r.problem]()}</p></div>}
      {r.status === "recording" && age !== null && age > 15000 && <div className="run-note warn" role="status"><p>{g.background
        ? t("Sem posição nova há {s} s. Vá para um local aberto.", { s: Math.round(age / 1000) })
        : t("Sem posição nova há {s} s. Mantenha a tela ligada e em local aberto.", { s: Math.round(age / 1000) })}</p></div>}
      {r.status === "recording" && r.lastAccuracy !== null && r.lastAccuracy > 30 && <div className="run-note warn" role="status"><p>{t("GPS impreciso ({m} m): estas leituras não somam distância.", { m: Math.round(r.lastAccuracy) })}</p></div>}

      {r.status === "idle" && halfway.map((x) => (
        <div key={x.id} className="run-note warn" role="alert">
          <p><strong>{t("Gravação interrompida")}</strong>: {metersToKm(x.distanceM)}, {formatDuration(x.activeS)}.</p>
          <p>{t("Ela volta pausada; o tempo em que o aparelho ficou sem gravar não conta.")}</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button variant="primary" onClick={() => void g.recover(x.id)}>{t("Retomar")}</Button>
            {dropId === x.id
              ? <><Button variant="danger" onClick={() => { setDropId(null); void g.removeSession(x.id); }}>{t("Sim, descartar")}</Button><Button onClick={() => setDropId(null)}>{t("Voltar")}</Button></>
              : <Button variant="danger" onClick={() => setDropId(x.id)}>{t("Descartar")}</Button>}
          </div>
        </div>
      ))}

      {(r.status === "idle" || r.status === "error") && !timedActive && <SportPicker sport={sport} onChange={setSport} />}
      {(r.status === "idle" || r.status === "error") && isTimed(sport) && (
        <TimedRecorder orgId={orgId} userId={me?.id ?? ""} sport={sport} onActive={setTimedActive} />
      )}

      {live && r.status !== "finalizing" && (
        <dl className="run-metrics" role="group" aria-label={t("Métricas da gravação")}>
          <div className="run-metric main"><dt>{t("Tempo ativo")}</dt><dd role="timer" aria-live="off">{formatDuration(act / 1000)}</dd></div>
          <div className="run-metric"><dt>{t("Distância")}</dt><dd>{metersToKm(dist)}</dd></div>
          <div className="run-metric"><dt>{t("Ritmo médio")}</dt><dd>{pace ? formatPace(pace) : "—"}</dd></div>
          <div className="run-metric"><dt>{t("Tempo decorrido")}</dt><dd style={{ fontSize: "1.25rem" }}>{formatDuration(elapsedMs(r, now) / 1000)}</dd></div>
          <div className="run-metric"><dt>{t("Última posição")}</dt><dd style={{ fontSize: "1.25rem" }}>{age === null ? "—" : t("há {s} s", { s: Math.round(age / 1000) })}</dd></div>
        </dl>
      )}
      {r.status === "recording" && !pace && dist < 100 && <p className="muted">{t("O ritmo aparece depois dos primeiros 100 m.")}</p>}
      {live && r.status !== "finalizing" && r.splits.length > 0 && (
        <p className="muted">{t("Parciais")}: {r.splits.map((s) => `${s.km} km ${formatPace(s.paceSPerKm)}`).join(" · ")}</p>
      )}

      {r.status === "finalizing" && (
        <section aria-labelledby="run-sum">
          <h2 id="run-sum" ref={title} tabIndex={-1} style={{ fontSize: "1.2rem" }}>{t("Resumo da corrida")}</h2>
          {enough ? (
            <>
              <dl className="run-metrics">
                <div className="run-metric main"><dt>{t("Distância")}</dt><dd>{metersToKm(dist)}</dd></div>
                <div className="run-metric"><dt>{t("Tempo ativo")}</dt><dd>{formatDuration(act / 1000)}</dd></div>
                <div className="run-metric"><dt>{t("Ritmo médio")}</dt><dd>{pace ? formatPace(pace) : "—"}</dd></div>
              </dl>
              {r.splits.length > 0 && (
                <table className="run-splits"><caption className="visually-hidden">{t("Parciais")}</caption>
                  <thead><tr><th scope="col">{t("Km")}</th><th scope="col">{t("Ritmo")}</th></tr></thead>
                  <tbody>{r.splits.map((s) => <tr key={s.km}><td>{s.km}</td><td>{formatPace(s.paceSPerKm)}</td></tr>)}</tbody>
                </table>
              )}
              <p className="muted">{t("Ao salvar, a gravação é guardada no aparelho e enviada ao servidor assim que houver conexão. O servidor recalcula a distância pelo percurso, então o valor final pode variar um pouco.")}</p>
              {body && body.points.some((p) => p.gap) && <p className="muted">{t("O percurso tem {l} pausa(s) ou perda(s) de sinal; esses trechos não somam distância.", { l: body.points.filter((p) => p.gap).length })}</p>}
            </>
          ) : (
            <div className="run-note warn" role="alert"><p>{t("Gravação curta demais para virar atividade (mínimo 2 pontos e 10 m).")}</p><p>{t("Você pode descartar e gravar de novo.")}</p></div>
          )}
        </section>
      )}

      <Modal
        open={savedOpen}
        title={t("Corrida salva")}
        onClose={() => { setSavedOpen(false); savedId.current = null; g.reset(); }}
        footer={
          <>
            <Button onClick={() => { setSavedOpen(false); savedId.current = null; g.reset(); }}>{t("Fechar")}</Button>
            <Button
              variant="primary"
              disabled={!savedActivity}
              busy={!savedActivity && g.syncing}
              onClick={() => { const id = savedActivity; setSavedOpen(false); savedId.current = null; g.reset(); if (id) navigate(`/org/${orgId}/atleta/atividades/${id}`); }}
            >
              {t("Ver atividade")}
            </Button>
          </>
        }
      >
        <p role="status">
          {savedActivity
            ? t("A corrida foi enviada e já está em Atividades.")
            : g.syncing
              ? t("Enviando ao servidor…")
              : t("A corrida ficou guardada no aparelho e será enviada assim que houver internet. Depois ela aparece em Atividades.")}
        </p>
        {g.sentDirect && <p className="muted">{t("O aparelho não conseguiu guardar uma cópia, mas o servidor confirmou o recebimento.")}</p>}
      </Modal>

      {askDiscard && live && (
        <div className="run-note danger" role="alertdialog" aria-label={t("Confirmar descarte")}>
          <p>{t("Descartar apaga esta gravação do aparelho. Não há outra cópia. Continuar?")}</p>
          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="danger" onClick={() => { setAskDiscard(false); void g.discard(); }}>{t("Sim, descartar")}</Button>
            <Button onClick={() => setAskDiscard(false)}>{t("Voltar")}</Button>
          </div>
        </div>
      )}
      {live && !askDiscard && <div><Button variant="ghost" onClick={() => setAskDiscard(true)}>{t("Descartar gravação")}</Button></div>}

      {r.status === "idle" && pendingCount > 0 && (
        <p className="muted" role="status">{tn("{n} gravação aguardando envio: ela vai sozinha quando houver internet.", "{n} gravações aguardando envio: elas vão sozinhas quando houver internet.", pendingCount, { n: pendingCount })}</p>
      )}
      {r.status === "idle" && problems.map((x) => (
        <div key={x.id} className="run-note danger" role="alert">
          <p><strong>{x.phase === "rejected" ? t("Recusada pelo servidor") : t("Antiga demais para o servidor (30 dias)")}</strong>: {metersToKm(x.distanceM)}, {formatDuration(x.activeS)}, {new Date(x.createdAt).toLocaleString()}.</p>
          {x.phase === "rejected" && x.lastError && <p>{x.lastError}</p>}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {dropId === x.id
              ? <><Button size="sm" variant="danger" onClick={() => { setDropId(null); void g.removeSession(x.id); }}>{t("Sim, descartar")}</Button><Button size="sm" onClick={() => setDropId(null)}>{t("Voltar")}</Button></>
              : <Button size="sm" variant="danger" onClick={() => setDropId(x.id)}>{t("Descartar")}</Button>}
          </div>
        </div>
      ))}

      {/* Esteira/Fortalecimento têm a própria barra de comandos (TimedRecorder): duas barras fixas se sobreporiam */}
      {!(isTimed(sport) && (r.status === "idle" || r.status === "error")) && <div className="run-controls">
        {(r.status === "idle" || r.status === "error") && !isTimed(sport) && <Button variant="primary" onClick={() => { if (r.status === "error") g.reset(); void g.start(); }} disabled={g.storage === "loading"}>{sport === "walk" ? t("Iniciar caminhada") : t("Iniciar corrida")}</Button>}
        {(r.status === "checking" || r.status === "requesting_location") && <Button onClick={() => void g.discard()}>{t("Cancelar")}</Button>}
        {r.status === "recording" && <><Button onClick={g.pause}>{t("Pausar")}</Button><Button variant="primary" onClick={g.finish}>{t("Finalizar")}</Button></>}
        {r.status === "paused" && <><Button variant="primary" onClick={g.resume}>{t("Retomar")}</Button><Button onClick={g.finish}>{t("Finalizar")}</Button></>}
        {r.status === "finalizing" && <Button variant="primary" disabled={!enough} onClick={() => { const id = r.clientId; void g.confirm(sport as GpsSport).then((ok) => { if (ok) { savedId.current = id; setSavedOpen(true); } }); }}>{t("Salvar atividade")}</Button>}
        {r.status === "saved" && !savedOpen && <Button variant="primary" onClick={g.reset}>{t("Nova gravação")}</Button>}
      </div>}
    </div>
  );
}
